import { Box, Skeleton, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Popover } from "antd";
import { useContext, useEffect, useState } from "react";
import dScribeJobsService from "../api/services/dScribeJobs-service";
import SharedTable, { TooltipCell } from "../components/shared-table";
import { showErrorToast } from "../helpers/utils";
import unauth from "../images/unauth.png";
import { PermissionsContext } from "../permission-context";

function AppNotificationLogs() {
  const [logs, setLogs] = useState([]);
  const { loading, error, hasPermission } = useContext(PermissionsContext);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [showTableSkeleton, setSkeleton] = useState(true);

  const fetchData = async () => {
    try {
      setSkeleton(true);
      const res = await dScribeJobsService.getVScribeAiNotificationLogs();

      const data = Array.isArray(res.data) ? res.data : [];
      setLogs(data);
    } catch (error) {
      console.log(error);
      showErrorToast("Failed to load notification logs. Please try again.");
    } finally {
      setSkeleton(false);
    }
  };

  useEffect(() => {
    fetchData();

    const handleResize = () => {
      // Standard Mobile/Tablet Breakpoint
      setIsSmallScreen(window.innerWidth < 768);
    };
    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const columns = [
    {
      title: "User Id",
      dataIndex: "userId",
      width: 150,
      fixed: "left",
      sorter: (a, b) => a.userId?.localeCompare(b.userId),
      render: (text) => {
        const contentStyle = {
          width: isSmallScreen ? "100px" : "100%",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          cursor: isSmallScreen ? "pointer" : "default",
          color: isSmallScreen ? "#1890ff" : "inherit",
        };
        return (
          <Popover
            content={<div style={{ color: "white" }}>{text}</div>}
            trigger={isSmallScreen ? "click" : "hover"}
            color="rgba(0, 0, 0, 0.85)"
          >
            <div style={contentStyle}>{text}</div>
          </Popover>
        );
      },
    },
    {
      title: "Account Id",
      dataIndex: "accountId",
      width: 120,
      sorter: (a, b) => a.accountId?.localeCompare(b.accountId),
      render: (text) => <TooltipCell text={text} width="100px" />,
    },
    {
      title: "Notification Type",
      dataIndex: "notificationType",
      width: 150,
      sorter: (a, b) => a.notificationType?.localeCompare(b.notificationType),
      render: (text) => <TooltipCell text={text} width="130px" />,
    },
    {
      title: "Expo Message Id",
      dataIndex: "expoMessageId",
      width: 200,
      render: (text) => <TooltipCell text={text} width="180px" />,
    },
    {
      title: "Delivery Status",
      dataIndex: "deliveryStatus",
      width: 150,
      sorter: (a, b) => a.deliveryStatus?.localeCompare(b.deliveryStatus),
      render: (text) => <TooltipCell text={text} width="130px" />,
    },
    {
      title: "Last Updated By",
      dataIndex: "lastUpdatedBy",
      width: 150,
      render: (text) => <TooltipCell text={text} width="130px" />,
    },
    {
      title: "Updated Date",
      dataIndex: "lastUpdatedDateTime",
      width: 180,
      sorter: (a, b) =>
        new Date(a.lastUpdatedDateTime || 0) -
        new Date(b.lastUpdatedDateTime || 0),
      render: (text) =>
        text
          ? new Date(text).toLocaleString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
              hour: "numeric",
              minute: "numeric",
            })
          : "-",
    },
  ];

  const finalColumns =
    logs.length === 0
      ? columns.map((col) => ({ ...col, fixed: undefined }))
      : columns;

  return (
    <>
      {!loading && !error && hasPermission("AppNotificationLogs") && (
        <Box style={{ marginRight: "1%", marginLeft: "2%" }}>
          <Grid
            sx={{
              marginTop: 1.5,
              borderBottom: 1,
              borderColor: "#dfdfdf",
            }}
          >
            <Typography
              gutterBottom
              fontWeight="700"
              lineHeight={"31.25px"}
              fontSize={"24px"}
              fontStyle={"DM Sans"}
            >
              Application Notification Logs
            </Typography>
          </Grid>
          <div
            style={{
              margin: "12px 0",
              fontFamily: "DM Sans",
              minHeight: "60vh",
            }}
          >
            {showTableSkeleton ? (
              <Skeleton
                variant="rectangular"
                width="100%"
                height="60vh"
                sx={{ borderRadius: "15px" }}
                animation="wave"
              />
            ) : (
              <SharedTable
                columns={finalColumns}
                dataSource={logs}
                scroll={{ x: "max-content" }}
                tableName="appNotificationLogs"
                rowKey="logId"
              />
            )}
          </div>
        </Box>
      )}
      {!loading && !error && !hasPermission("AppNotificationLogs") && (
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "90vh",
          }}
        >
          <img
            src={unauth}
            alt="UnAuthorized"
            style={{ maxWidth: "100%", maxHeight: "100%" }}
          />
        </Box>
      )}
    </>
  );
}

export default AppNotificationLogs;
